import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Bell, Calendar, Check, Clock } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr, es, enUS } from 'date-fns/locale';
import axios from 'axios';

import { API } from '../config/api';

export default function Notifications() {
  const { t, language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const locales = { fr, es, en: enUS };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchNotifications();
  }, [isAuthenticated]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      window.dispatchEvent(new Event('puro-notif-updated'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleClaimSpot = async (classId, notificationId) => {
    try {
      await axios.post(`${API}/waitlist/claim/${classId}`, {}, { withCredentials: true });
      toast.success(t('success'), { description: 'Place réservée avec succès !' });
      handleMarkAsRead(notificationId);
      fetchNotifications();
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail });
    }
  };

  const getTimeAgo = (dateStr) => {
    return formatDistanceToNow(new Date(dateStr), { 
      addSuffix: true, 
      locale: locales[language] || locales.fr 
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="font-heading font-light text-3xl sm:text-4xl text-[#2C2A29] tracking-tight mb-8" data-testid="notifications-title">
        {t('notifications')}
      </h1>

      {notifications.length === 0 ? (
        <Card className="bg-white border-[#E9DDCC] rounded-2xl">
          <CardContent className="py-16 text-center">
            <Bell className="w-12 h-12 text-[#D7CFC4] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#7A6455]">{t('noNotifications')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification, index) => (
            <Card 
              key={notification.id}
              className={`border-[#E9DDCC] rounded-2xl transition-all ${
                notification.read ? 'bg-white' : 'bg-[#F5F2EA]'
              } animate-fade-in`}
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`notification-${notification.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    notification.type === 'waitlist_available' 
                      ? 'bg-[#8C9B86]/20' 
                      : 'bg-[#C99A55]/20'
                  }`}>
                    {notification.type === 'waitlist_available' ? (
                      <Calendar className="w-5 h-5 text-[#8C9B86]" strokeWidth={1.5} />
                    ) : (
                      <Bell className="w-5 h-5 text-[#C99A55]" strokeWidth={1.5} />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-medium text-[#2C2A29]">{notification.title}</h3>
                      <span className="text-xs text-[#7A6455] whitespace-nowrap">
                        {getTimeAgo(notification.created_at)}
                      </span>
                    </div>
                    
                    <p className="text-sm text-[#7A6455] mt-1">{notification.message}</p>
                    
                    {notification.type === 'waitlist_available' && notification.class_id && !notification.read && (
                      <div className="mt-3 flex items-center gap-2">
                        <Button
                          onClick={() => handleClaimSpot(notification.class_id, notification.id)}
                          className="btn-primary text-sm h-9"
                          data-testid={`claim-spot-${notification.id}`}
                        >
                          Réserver la place
                        </Button>
                        <span className="text-xs text-[#C76542] flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          Offre limitée
                        </span>
                      </div>
                    )}
                    
                    {!notification.read && notification.type !== 'waitlist_available' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMarkAsRead(notification.id)}
                        className="text-[#C99A55] mt-2 h-8"
                        data-testid={`mark-read-${notification.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" strokeWidth={1.5} />
                        {t('markAsRead')}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
